import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Copy, FileText } from 'lucide-react';
import { 
  Button, 
  SearchBar, 
  StatusBadge,
  LoadingSpinner 
} from '../../components/common';
import { Navigation } from '../../components/layouts';
import { TemplateForm } from '../../components/forms';
import { templateService } from '../../services/templateService';

const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [error, setError] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const templatesData = await templateService.getAll();
      setTemplates(templatesData || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
      setError('Nu s-au putut încărca template-urile. Verificați conexiunea la server.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSubmit = async (templateData) => {
    try {
      setError(null);
      
      if (editingTemplate) {
        // Update existing template
        await templateService.update(editingTemplate.id, templateData);
        console.log('Template updated successfully:', editingTemplate.id);
      } else {
        // Create new template
        await templateService.create(templateData);
        console.log('Template created successfully');
      }
      
      // Reload templates list
      await loadTemplates();
      setShowTemplateForm(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Failed to save template:', error);
      setError(editingTemplate ? 'Nu s-a putut actualiza template-ul.' : 'Nu s-a putut crea template-ul.');
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowTemplateForm(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    const template = templates.find(t => t.id === templateId);
    const contractCount = template?.contractCount || 0;
    
    if (contractCount > 0) {
      alert(`Nu se poate șterge template-ul deoarece este folosit în ${contractCount} contracte.`);
      return;
    }

    if (window.confirm('Sunteți sigur că doriți să ștergeți acest template?')) {
      try {
        setError(null);
        await templateService.delete(templateId);
        console.log('Template deleted successfully:', templateId);
        
        // Reload templates list
        await loadTemplates();
      } catch (error) {
        console.error('Failed to delete template:', error);
        setError('Nu s-a putut șterge template-ul.');
      }
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      setError(null);
      
      // Create a copy of the template with modified name
      const duplicateData = {
        name: `${template.name} - Copie`,
        content: template.content || template.contentPreview,
        isActive: false // Start as inactive
      };
      
      await templateService.create(duplicateData);
      console.log('Template duplicated successfully:', template.id);
      
      // Reload templates list
      await loadTemplates();
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      setError('Nu s-a putut duplica template-ul.');
    }
  };

  const handleToggleActive = async (templateId, isActive) => {
    try {
      setError(null);
      
      if (isActive) {
        // Deactivate template
        await templateService.deactivate(templateId);
        console.log('Template deactivated successfully:', templateId);
      } else {
        // Activate template - update with isActive: true
        const template = templates.find(t => t.id === templateId);
        if (template) {
          await templateService.update(templateId, { ...template, isActive: true });
          console.log('Template activated successfully:', templateId);
        }
      }
      
      // Reload templates list
      await loadTemplates();
    } catch (error) {
      console.error('Failed to toggle template status:', error);
      setError('Nu s-a putut schimba statusul template-ului.');
    }
  };

  const handlePreviewTemplate = async (templateId) => {
    try {
      setError(null);
      const templateData = await templateService.getById(templateId);
      setPreviewTemplate(templateData);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Failed to load template for preview:', error);
      setError('Nu s-a putut încărca template-ul pentru previzualizare.');
    }
  };

  // Create content preview (first 200 characters)
  const createContentPreview = (content) => {
    if (!content) return 'Fără conținut...';
    const cleanContent = content.replace(/\n/g, ' ').trim();
    return cleanContent.length > 200 ? cleanContent.substring(0, 200) + '...' : cleanContent;
  };

  // Format date with fallback
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('ro-RO');
    } catch (error) {
      return 'N/A';
    }
  };

  // Filter templates based on search term
  const filteredTemplates = templates.filter(template =>
    template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.contentPreview?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show loading state during initial load
  if (loading && templates.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Se încarcă template-urile..." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Navigation
        title="Gestiune Template-uri"
        subtitle={`${templates.length} template-uri pentru generarea contractelor`}
        actions={[
          {
            label: 'Template Nou',
            icon: Plus,
            variant: 'primary',
            onClick: () => {
              setEditingTemplate(null);
              setShowTemplateForm(true);
            }
          }
        ]}
      />

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-sm text-red-700">
              {error}
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <SearchBar
          placeholder="Căutați template-uri după nume sau conținut..."
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={() => setSearchTerm('')}
        />
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {template.name || 'Template fără nume'}
                  </h3>
                  <StatusBadge 
                    status={template.isActive ? 'Activ' : 'Inactiv'}
                    size="sm"
                  />
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                  <span>{template.contractCount || 0} contracte</span>
                  <span>•</span>
                  <span>Actualizat: {formatDate(template.updatedAt)}</span>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="mb-4">
              <div className="bg-gray-50 rounded-md p-3 border">
                <div className="text-xs font-medium text-gray-700 mb-2">Preview:</div>
                <div className="text-xs text-gray-600 font-mono leading-relaxed max-h-24 overflow-hidden">
                  {createContentPreview(template.content || template.contentPreview)}
                </div>
                {(template.content || template.contentPreview) && (
                  <div className="text-xs text-gray-500 mt-1">...</div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  icon={Eye}
                  onClick={() => handlePreviewTemplate(template.id)}
                  title="Previzualizare completă"
                >
                  Preview
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  icon={Copy}
                  onClick={() => handleDuplicateTemplate(template)}
                  title="Duplică template"
                >
                  Duplică
                </Button>
              </div>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant={template.isActive ? 'outline' : 'secondary'}
                  onClick={() => handleToggleActive(template.id, template.isActive)}
                  title={template.isActive ? 'Dezactivează template' : 'Activează template'}
                >
                  {template.isActive ? 'Dezactivează' : 'Activează'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  icon={Edit}
                  onClick={() => handleEditTemplate(template)}
                  title="Editează template"
                />
                <Button
                  size="sm"
                  variant="danger"
                  icon={Trash2}
                  onClick={() => handleDeleteTemplate(template.id)}
                  disabled={(template.contractCount || 0) > 0}
                  title={
                    (template.contractCount || 0) > 0 
                      ? `Nu se poate șterge - folosit în ${template.contractCount} contracte` 
                      : 'Șterge template'
                  }
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredTemplates.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nu au fost găsite template-uri</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm ? 'Încercați să modificați termenii de căutare.' : 'Începeți prin a crea primul template.'}
          </p>
          {!searchTerm && (
            <Button
              icon={Plus}
              onClick={() => {
                setEditingTemplate(null);
                setShowTemplateForm(true);
              }}
            >
              Creează Primul Template
            </Button>
          )}
        </div>
      )}

      {/* Loading Overlay */}
      {loading && templates.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <LoadingSpinner size="md" text="Se actualizează template-urile..." />
          </div>
        </div>
      )}

      {/* Template Form Modal */}
      <TemplateForm
        template={editingTemplate}
        isOpen={showTemplateForm}
        onClose={() => {
          setShowTemplateForm(false);
          setEditingTemplate(null);
        }}
        onSubmit={handleTemplateSubmit}
      />

      {/* Preview Modal */}
      {showPreviewModal && previewTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Previzualizare Template
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {previewTemplate.name}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewTemplate(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
              <div className="bg-gray-50 rounded-lg p-4 border">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono leading-relaxed">
                  {previewTemplate.content || 'Fără conținut disponibil...'}
                </pre>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewTemplate(null);
                }}
              >
                Închide
              </Button>
              <Button
                variant="primary"
                icon={Edit}
                onClick={() => {
                  setShowPreviewModal(false);
                  handleEditTemplate(previewTemplate);
                  setPreviewTemplate(null);
                }}
              >
                Editează Template
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatesPage;